import { useEffect, useMemo } from "react";
import { ApolloError, useQuery } from "@apollo/client";
import {
  GET_APPLICABLE_NOTICE_VIOLATION_TYPES,
  GET_APPLICABLE_CITATION_VIOLATION_TYPES,
  GetApplicableViolationTypesData,
  GetApplicableViolationTypesVars,
} from "common/graphql";

export type ApplicableViolationTypesSource = "notices" | "citations";

const QUERY_BY_SOURCE = {
  notices: GET_APPLICABLE_NOTICE_VIOLATION_TYPES,
  citations: GET_APPLICABLE_CITATION_VIOLATION_TYPES,
};

export interface UseApplicableViolationTypesResult {
  /** Distinct violation_type values matching `filter`, or undefined while loading. */
  applicableViolationTypes?: string[];
  error?: ApolloError;
}

/**
 * Returns the distinct violation types that actually occur for the current account, scoped by
 * Hasura's existing account-level row permissions on enforcement.intent / enforcement.citation.
 * Pass the same base `filter` used by the list this dropdown is filtering (e.g. a notices
 * variant's status filter, or citations' notice_sent_at requirement) so the values returned
 * here can never produce zero results under that same filter.
 */
export function useApplicableViolationTypes(
  source: ApplicableViolationTypesSource,
  filter?: GetApplicableViolationTypesVars["filter"],
): UseApplicableViolationTypesResult {
  const { data, error } = useQuery<GetApplicableViolationTypesData, GetApplicableViolationTypesVars>(
    QUERY_BY_SOURCE[source],
    {
      fetchPolicy: "cache-first",
      variables: { filter },
    },
  );

  const applicableViolationTypes = useMemo(
    () => data?.applicableViolationTypes.map((row) => row.violationType),
    [data],
  );

  // Deliberately fails open to the full, unfiltered list on error (a wrong-but-safe filter
  // dropdown beats a broken page) — warn so the failure doesn't go completely unnoticed.
  useEffect(() => {
    if (error) {
      console.warn("useApplicableViolationTypes: query failed, falling back to the full violation type list", error);
    }
  }, [error]);

  return { applicableViolationTypes, error };
}
